import { ActionIcon, Anchor, Group, Table, Tooltip } from '@mantine/core'
import { IconRefresh, IconRouter } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { Link, useLocation } from 'react-router'
import { RouterPath, replacePathParams } from '@/router/paths'
import { Device } from '@/types/device'
import {
  getDeepStreamVersion,
  getDeviceGpus,
  getEdgeClientVersion,
  getLastEdgeClientActionErrors,
  isJetsonDevice
} from '@/utils/device/getDeviceAttr'
import { DeepStreamVersionBadge } from '../DeepStreamVersionBadge/DeepStreamVersionBadge'
import { DeviceTypeBadge } from '../DeviceTypeBadge/DeviceTypeBadge'
import { EdgeClientActionFailureIndicator } from '../EdgeClientActionFailureIndicator/EdgeClientActionFailureIndicator'
import { EdgeClientVersionBadge } from '../EdgeClientVersionBadge/EdgeClientVersionBadge'
import { GpuBadgeList } from '../GpuList/GpuBadgeList'
import { OnlineStatus } from '../OnlineStatus/OnlineStatus'
import { useEdgeClientActions } from '../useEdgeClientActions'

type DeviceListItemProps = {
  device: Device
  showActions?: boolean
  latestEdgeClientVersion?: string
  onActionSettled?: () => void
}

export const DeviceListItem = ({
  device,
  showActions,
  latestEdgeClientVersion,
  onActionSettled
}: DeviceListItemProps) => {
  const location = useLocation()

  const { triggerUpdate, triggerRestart, isUpdating, isRestarting } =
    useEdgeClientActions({ deviceId: device.id, onActionSettled })

  const currentEdgeClientVersion = getEdgeClientVersion(device)
  const deepStreamVersion = getDeepStreamVersion(device)
  const isJetson = isJetsonDevice(device)
  const lastActionErrors = getLastEdgeClientActionErrors(device)

  return (
    <Table.Tr>
      <Table.Td>
        <IconRouter size={20} />
      </Table.Td>

      <Table.Td>
        <Anchor
          component={Link}
          size="sm"
          underline="hover"
          c="blue.8"
          fw="bold"
          to={replacePathParams(RouterPath.deviceDetails, {
            deviceId: device.id
          })}
          state={{ deviceListParams: location.search }}
        >
          {device.label}
        </Anchor>
      </Table.Td>

      <Table.Td>
        <DeviceTypeBadge isJetson={isJetson} />
      </Table.Td>

      <Table.Td>
        <OnlineStatus status={device.active ? 'online' : 'offline'} />
      </Table.Td>

      <Table.Td>
        <GpuBadgeList gpus={getDeviceGpus(device)} isJetson={isJetson} />
      </Table.Td>

      <Table.Td>
        {(currentEdgeClientVersion || lastActionErrors.update) && (
          <Group wrap="nowrap" gap="xs" align="center">
            <EdgeClientVersionBadge
              version={currentEdgeClientVersion}
              latestVersion={latestEdgeClientVersion}
              isLatest={
                Boolean(currentEdgeClientVersion) &&
                currentEdgeClientVersion === latestEdgeClientVersion
              }
              showInstallButton={showActions && device.active}
              isDisabled={isRestarting || isUpdating}
              isLoading={isUpdating}
              onUpdate={() => void triggerUpdate()}
            />
            {lastActionErrors.update && (
              <EdgeClientActionFailureIndicator
                error={lastActionErrors.update}
              />
            )}
          </Group>
        )}
      </Table.Td>

      <Table.Td>
        <DeepStreamVersionBadge version={deepStreamVersion} />
      </Table.Td>

      <Table.Td>
        <Group wrap="nowrap" gap={4} justify="end">
          {lastActionErrors.restart && (
            <EdgeClientActionFailureIndicator
              error={lastActionErrors.restart}
            />
          )}
          {showActions && (
            <Tooltip
              label={<FormattedMessage id="devices.list.buttons.restart" />}
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                radius="xl"
                disabled={!device.active || isRestarting || isUpdating}
                loading={isRestarting}
                onClick={() => void triggerRestart()}
              >
                <IconRefresh style={{ width: '80%', height: '80%' }} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  )
}
